import { IDENTITY_DIRECTORY_CHANGED } from "./IdentityAssignmentFields";
import { useEffect, useState } from "react";
import { academyRequest } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type { TenancyMemberPage, TenancyMembership } from "./generated/tenancy";
import type { IdentityWorkspaceDirectory } from "./generated/native-identity";

const memberRoutes = new Set(["people-members", "people-directory"]);
const grantRoutes = new Set(["people-roles", "people-access", "roles-permissions"]);
const invitationRoutes = new Set(["invitations"]);
export const isIdentityDirectory = (route: string) =>
  memberRoutes.has(route) || grantRoutes.has(route) || invitationRoutes.has(route);
export type IdentityDirectoryRow = Record<string, string | number | boolean>;
export function useIdentityDirectory(route: string) {
  const account = useIdentity();
  const context = account.session
    ? `${account.session.tenantId}:${account.session.user.id}:${account.session.workspace.id}`
    : "";
  const membership = account.access?.memberships.find(m => m.workspaceId === account.session?.workspace.id);
  const kind = membership?.organizationId
    ? "organization"
    : membership?.institutionId
      ? "institution"
      : undefined;
  const entityId = membership?.organizationId || membership?.institutionId;
  const [loaded, setLoaded] = useState<{
    context: string;
    data: IdentityWorkspaceDirectory | null;
    entityMembers?: TenancyMemberPage;
    error: string;
  } | null>(null);
  const enabled = isIdentityDirectory(route);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRevision(v => v + 1);
    window.addEventListener(IDENTITY_DIRECTORY_CHANGED, refresh);
    return () => window.removeEventListener(IDENTITY_DIRECTORY_CHANGED, refresh);
  }, []);
  useEffect(() => {
    if (!enabled || !context) return;
    let active = true;
    void (async () => {
      try {
        const before = await checkIdentitySession();
        if (`${before.tenantId}:${before.user.id}:${before.workspace.id}` !== context)
          throw new Error("Account context changed. Reload the directory.");
        if (kind && entityId && memberRoutes.has(route)) {
          const items: TenancyMembership[] = [];
          let after: string | null = null;
          const visited = new Set<string>();
          do {
            const page: TenancyMemberPage = await academyRequest(
              `tenancy/${kind}/${entityId}/members?limit=100${after ? `&after=${after}` : ""}`,
            );
            items.push(...page.items);
            after = page.nextCursor;
            if (after && visited.has(after)) throw new Error("Member pagination did not advance.");
            if (after) visited.add(after);
          } while (after);
          const session = await checkIdentitySession();
          if (
            session.user.id !== before.user.id ||
            session.tenantId !== before.tenantId ||
            session.workspace.id !== before.workspace.id
          )
            throw new Error("Account context changed. Reload the directory.");
          if (active)
            setLoaded({
              context,
              data: null,
              entityMembers: { items, hasMore: false, nextCursor: null },
              error: "",
            });
          return;
        }
        const data = await academyRequest<IdentityWorkspaceDirectory>("identity/workspace-directory");
        const session = await checkIdentitySession();
        if (data.workspaceId !== session.workspace.id)
          throw new Error("Workspace changed. Reload the directory.");
        if (active) setLoaded({ context, data, error: "" });
      } catch (error) {
        if (active)
          setLoaded({
            context,
            data: null,
            error: error instanceof Error ? error.message : "Unable to load identity directory.",
          });
      }
    })();
    return () => {
      active = false;
    };
  }, [context, enabled, entityId, kind, revision, route]);
  const result = loaded?.context === context ? loaded : null;
  const data = result?.data;
  const workspace = account.session?.workspace.name || "";
  const scope = (type: string | null, id: string | null) =>
    type ? `${type}${id ? ` · ${id}` : ""}` : workspace;
  let rows: IdentityDirectoryRow[] = [];
  if (memberRoutes.has(route))
    rows = (data?.members || []).map(m => ({
      id: m.id,
      code: m.userId,
      name: m.name,
      email: m.email,
      owner: workspace,
      scope: workspace,
      status: m.status,
      role: m.roles.join(", ") || "No active role",
      updated: new Date(m.endedAt || m.joinedAt).toLocaleString(),
    }));
  if (memberRoutes.has(route) && result?.entityMembers)
    rows = result.entityMembers.items.map(m => ({
      id: m.id,
      code: m.personId,
      name: m.name || "Person details unavailable",
      owner: workspace,
      scope: `${m.scopeType} · ${m.scopeId}`,
      status: m.status,
      membershipType: m.membershipType,
      scopeType: m.scopeType,
      scopeId: m.scopeId,
      accountId: m.accountId || "",
      validFrom: m.validFrom,
      validUntil: m.validUntil || "",
      personId: m.personId,
      version: m.version,
      updated: new Date(m.validFrom).toLocaleString(),
      expires: m.validUntil ? new Date(m.validUntil).toLocaleString() : "No expiry",
    }));
  if (grantRoutes.has(route))
    rows = (data?.grants || []).map(g => ({
      id: g.id,
      code: g.roleKey,
      name: `${g.name} · ${g.roleName}`,
      owner: g.email,
      scope: scope(g.scopeType, g.scopeId),
      status: "Active",
      updated: new Date(g.grantedAt).toLocaleString(),
      permissions: g.permissions.join(", "),
      expires: g.expiresAt ? new Date(g.expiresAt).toLocaleString() : "No expiry",
    }));
  if (invitationRoutes.has(route))
    rows = (data?.invitations || []).map(i => ({
      id: i.id,
      code: i.id,
      name: i.email,
      owner: i.invitedBy,
      role: i.roleName,
      scope: scope(i.scopeType, i.scopeId),
      status: i.status,
      updated: new Date(i.createdAt).toLocaleString(),
      expires: new Date(i.expiresAt).toLocaleString(),
    }));
  return {
    enabled,
    rows,
    data: data || result?.entityMembers,
    error: result?.error || account.error,
    loading: enabled && !result,
    account,
  };
}
